import React, { Suspense, useRef, useEffect, useCallback } from 'react';
import {
  Route,
  Switch,
  useHistory,
  Redirect,
  useLocation
} from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import { Carousel } from 'react-responsive-carousel';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { format, parseISO } from 'date-fns';

// constants
import LocalStrings from '../../assets/languages/index.js';
import Loader from '../../components/Common/Loader';
import { CDN_URL, DATE_FORMAT_FNS } from '../../constants/constant.js';
import {
  OutsideClickDetector,
  getAppTabId,
  getSectionListingSortType,
  getThumbImageUrl,
  getUserGroupIds,
  getUserRolePrivilege,
  isJSONParsable
} from '../../util/util-common.js';
import { ALLOWED_FEATURE_KEYS } from '../../constants/section-constants/common-constants.js';
import {
  ALLOWED_AD_INTERVALS,
  ALLOWED_POST_ANALYTICS_TYPES
} from '../../constants/section-constants/type-constants.js';
import {
  showError,
  showToast
} from '../../components/Common/alertmessage/index.js';
import {
  ALLOWED_ADVERTISEMENT_REDIRECTIONS,
  ROLE_PRIVILEGE_SPECIAL_CASES
} from '../../constants/section-constants/redirection-constants.js';
import { ALLOWED_SCHEDULEDATE_APICALL_SOURCE } from '../../constants/section-constants/source-constant';
import { ALLOWED_GAME_CHALLENGE_TYPES } from '../../constants/section-constants/gamification-constants.js';

// actions
import {
  redirectingCalledFrom,
  postAnalytics,
  fetchUserAccess,
  fetchInterests,
  fetchAppuserCategory,
  activateNavbarLineLoader
} from '../../actions';
import {
  setSponsor,
  clearAllSponsor,
  clearSponsorDetails,
  fetchAllSponsor
} from '../../components/sponsors/actions';
import {
  setExhibitor,
  clearAllExhibitor,
  clearExhibitorDetails,
  fetchAllExhibitor
} from '../../components/exhibitors/actions';
import {
  openSpeakerDetails,
  fetchAllSpeakers
} from '../../components/speakers/actions';
import { fetchAttendeeList } from '../../components/attendee/actions';
import {
  makeEmpty_ScheduleData,
  makeEmpty_ScheduleTracks,
  makeEmpty_TrackSessions,
  fetchMinDataOfSelectedSchedule,
  fetchScheduleData,
  fetchBookedSchedule,
  selectScheduleDate,
  fetchScheduleTracks,
  selectScheduleTrack,
  fetchTrackSessions,
  setSessionDetailsAction
} from '../../components/sessions/actions';
import {
  fetchGameChallengeStatus,
  setGamificationPopup
} from '../../components/gamification/actions';

// components
import AdminNavbar from '../../components/Navbars/AdminNavbar.js';
import SideBar from '../../components/Sidebar/Sidebar.js';
import FourZeroFour from '../../components/Common/pagenotfound.js';

// files
import NoImgIcon from '../../assets/img/placeholders/NoImg.svg';

const EventPortal = props => {
  const {
    // state
    isLoggedIn,
    session,
    eventDetails,
    userDetails,
    deviceSize,
    showSideBar,
    sideAdvtList,
    featureAppTabIds,
    sideMenuAdInterval,
    attendeeList,
    listSortType,
    appuserCategories,
    interestList,
    rolePermissions,
    featureSortType,
    groupIds,

    // components
    routesNew,
    renderSidebar,
    sideAdPostAnalyticsCalled,
    // functions
    setSideAdPostAnalyticsCalled,
    handleEditProfileToggle
  } = props;

  const location = useLocation();
  const history = useHistory();
  const sidenavRef = useRef(null);
  const sideNavOutsideClicked = OutsideClickDetector(sidenavRef, showSideBar);

  useEffect(() => {
    if (_.isEmpty(attendeeList?.data) && !_.isEmpty(session?.sessionToken))
      props.fetchAttendeeList(session, { sort: listSortType }, res => {});
  }, []);

  (() => {
    if (!_.isEmpty(sideAdvtList) && isLoggedIn && !sideAdPostAnalyticsCalled) {
      setSideAdPostAnalyticsCalled(true);
    }
  })();

  const handleAdSelected = adItem => {
    const selectedAd = sideAdvtList[adItem];

    if (
      !_.includes(
        ALLOWED_ADVERTISEMENT_REDIRECTIONS,
        selectedAd?.internalscreen
      )
    )
      return;
    else if (!rolePermissions[selectedAd?.internalscreen]) {
      showError(
        LocalStrings.dynamic.kAccessDeniedDesc,
        LocalStrings.dynamic.kAccessDenied
      );
      return;
    }

    props.postAnalytics(
      session,
      [
        ALLOWED_POST_ANALYTICS_TYPES.screen.sidemenu,
        ALLOWED_POST_ANALYTICS_TYPES.type.adClick,
        selectedAd.advtid,
        null,
        0
      ],
      res => {}
    );

    switch (selectedAd?.internalscreen) {
      case ALLOWED_FEATURE_KEYS.exhibitor:
        props.redirectingCalledFrom('admin-ad');
        // --- clear
        props.setExhibitor({});
        props.clearExhibitorDetails();
        // --- redirection
        history.push('/exhibitors/' + featureAppTabIds.exhibitor);
        // --- fetch
        props.fetchAllExhibitor(
          session,
          {
            type: 'redirection',
            id: selectedAd.relatedid,
            sort: featureSortType.exhibitor,
            eventDetails
          },
          res => {}
        );
        break;

      case ALLOWED_FEATURE_KEYS.sponsor:
        props.redirectingCalledFrom('admin-ad');
        // --- clear
        props.setSponsor({});
        props.clearSponsorDetails();
        // --- redirection
        history.push('/sponsors/' + featureAppTabIds.sponsor);
        // --- fetch
        props.fetchAllSponsor(
          session,
          {
            type: 'redirection',
            id: selectedAd.relatedid,
            sort: featureSortType.sponsor,
            eventDetails
          },
          res => {}
        );

        // Post Analytics Data
        props.postAnalytics(
          session,
          [
            ALLOWED_POST_ANALYTICS_TYPES.screen.sponsor,
            ALLOWED_POST_ANALYTICS_TYPES.type.entry,
            selectedAd.relatedid,
            null,
            0
          ],
          res => {}
        );

        // Gamification Status
        props.fetchGameChallengeStatus(
          session,
          ALLOWED_GAME_CHALLENGE_TYPES.types.sponsor,
          response => {
            if (response.data && response.data.iscompleted) {
              if (response.data.showpopup) {
                response.data.challengetype =
                  ALLOWED_GAME_CHALLENGE_TYPES.types.sponsor;
                props.setGamificationPopup(true, response.data);
              } else if (response.data?.showpopup) {
                showToast(
                  `${LocalStrings.dynamic.kYouHaveGot} ${response.data.points} ${LocalStrings.dynamic.kPointsText}`
                );
              }
            }
          }
        );
        break;

      case ALLOWED_FEATURE_KEYS.speaker:
        props.redirectingCalledFrom('admin-ad');
        // --- clear
        props.openSpeakerDetails(null, {});
        // --- redirection
        history.push('/speakers/' + featureAppTabIds.speaker);
        // --- Fetch
        props.fetchAllSpeakers(
          session,
          {
            type: 'redirection',
            id: selectedAd.relatedid,
            sort: featureSortType.speaker
          },
          res => {}
        );

        // Post Analytics Data
        props.postAnalytics(
          session,
          [
            ALLOWED_POST_ANALYTICS_TYPES.screen.speakers,
            ALLOWED_POST_ANALYTICS_TYPES.type.entry,
            selectedAd.relatedid,
            null,
            0
          ],
          res => {}
        );

        // Gamification Status
        props.fetchGameChallengeStatus(
          session,
          ALLOWED_GAME_CHALLENGE_TYPES.types.speakers,
          response => {
            if (response.data && response.data.iscompleted) {
              if (response.data.showpopup) {
                response.data.challengetype =
                  ALLOWED_GAME_CHALLENGE_TYPES.types.speakers;
                props.setGamificationPopup(true, response.data);
              } else if (response.data?.showpopup) {
                showToast(
                  `${LocalStrings.dynamic.kYouHaveGot} ${response.data.points} ${LocalStrings.dynamic.kPointsText}`
                );
              }
            }
          }
        );
        break;

      case ALLOWED_FEATURE_KEYS.schedule:
        props.activateNavbarLineLoader(true);
        props.fetchMinDataOfSelectedSchedule(
          session,
          {
            sessionId: selectedAd?.relatedid,
            apptabId: featureAppTabIds.schedule
          },
          res => {
            props.activateNavbarLineLoader(false);

            if (res.success && !_.isEmpty(res?.data)) {
              // necessary actions
              props.redirectingCalledFrom(
                ALLOWED_SCHEDULEDATE_APICALL_SOURCE.ads
              );
              props.makeEmpty_ScheduleData();
              props.makeEmpty_ScheduleTracks();
              props.makeEmpty_TrackSessions();
              props.fetchBookedSchedule(session, res => {});
              props.fetchUserAccess(session, res => {});
              _.isEmpty(interestList) &&
                props.fetchInterests(session, res => {});
              _.isEmpty(appuserCategories) &&
                props.fetchAppuserCategory(session, res => {});

              // --- redirection
              history.push('/sessions/' + featureAppTabIds.schedule);

              const selectedData = res?.data;
              const selectedDataDate = selectedData?.starttime
                ? format(parseISO(selectedData.starttime), DATE_FORMAT_FNS)
                : '';
              // select the schedule dates
              props.fetchScheduleData(
                session,
                {
                  source: ALLOWED_SCHEDULEDATE_APICALL_SOURCE.ads,
                  selectedDate: selectedDataDate,
                  apptabId: featureAppTabIds.schedule,
                  groupIds
                },
                res => {
                  if (res.success) {
                    let filterArr =
                      !_.isEmpty(res.result) && res.result.response.length
                        ? _.filter(res.result.response, sess => {
                            return sess?.scheduledate === selectedDataDate;
                          })
                        : [];

                    if (filterArr.length) {
                      props.selectScheduleDate(filterArr[0]); // session page date selection
                      // fetch tracks according to that date
                      props.fetchScheduleTracks(
                        session,
                        filterArr[0],
                        {
                          source: ALLOWED_SCHEDULEDATE_APICALL_SOURCE.ads,
                          apptabId: featureAppTabIds.schedule
                        },
                        res => {
                          if (res.success) {
                            let myTrack = _.find(
                              filterArr[0]?.ScheduleDateToScheduletracks,
                              track => {
                                return (
                                  track.scheduletrackid ==
                                  selectedData?.scheduletrackid
                                );
                              }
                            );

                            if (!_.isEmpty(myTrack)) {
                              props.selectScheduleTrack(myTrack); // session page track selection
                            }
                          }
                        }
                      );

                      // fetch sessions for the selected session's track
                      props.fetchTrackSessions(
                        session,
                        {
                          scheduletrackid: selectedData.scheduletrackid,
                          apptabId: featureAppTabIds.schedule
                        },
                        res => {
                          if (res.success) {
                            let mySession = _.find(
                              res.result.response,
                              sess => {
                                return sess.sessionid == selectedData.sessionid;
                              }
                            );

                            if (!_.isEmpty(mySession))
                              props.setSessionDetailsAction(mySession); // session page session selection
                          }
                        }
                      );
                    }
                  } else {
                    showError(LocalStrings.dynamic.kSomethingWentWrongMsg);
                  }
                }
              );
            } else {
              showError(LocalStrings.dynamic.kSomethingWentWrongMsg);
            }
          }
        );
        break;

      default:
        break;
    }
  };

  const _renderRoutes = useCallback(() => {
    let matched = false;

    return (
      <Suspense fallback={<Loader />}>
        <Switch>
          {_.map(routesNew, prop => {
            if (isLoggedIn) {
              if (!prop.hasAccess) return;
              // if (
              //   !_.isEmpty(userDetails) &&
              //   userDetails?.comesfrom !== 'ticket' &&
              //   prop.path === '/mytickets'
              // )
              //   return;
            } else if (
              _.includes(['/autologin', '/sso-auth', '/overview'], prop.path)
            ) {
              // do nothing
            } else
              return (
                <Route
                  key={prop.path}
                  path={prop.path}
                  render={() => <Redirect to="/overview" />}
                />
              );

            return (
              <Route
                key={prop.path}
                path={prop.path}
                component={prop.component}
              />
            );
          })}

          {/* gibberish url 404 redirect */}
          {!matched &&
            _.map(routesNew, (r, key) => {
              if (r.path === location.pathname) {
                matched = true;
              } else {
                let filter = _.filter(
                  routesNew,
                  r => r?.path == location.pathname
                );
                if (!filter.length) {
                  return <FourZeroFour key={`key_${key}`} />;
                }
              }
            })}
        </Switch>
      </Suspense>
    );
  }, [routesNew, isLoggedIn, userDetails]);

  const _renderSideBar = () => {
    const isMobile = deviceSize.isMobile;
    const isTab = deviceSize.isTab;

    return (
      <SideBar
        {...props}
        routes={routesNew}
        isMobile={isMobile}
        isTab={isTab}
        componentHeight={
          isMobile
            ? !_.isEmpty(sideAdvtList) && deviceSize.height != 1024
            : null
        }
        handleEditProfileToggle={
          isMobile ? type => handleEditProfileToggle(type) : () => {}
        }
        sideNavOutsideClicked={isMobile && sideNavOutsideClicked}
      />
    );
  };

  return (
    <div
      className={`row full-height${
        deviceSize.isMobile && isLoggedIn ? '' : ' mx-0'
      }`}
    >
      {/* --------------- sidebar section start -------------- */}

      {/* ---------- controlling sidebar for mobile call and not loading ------- */}
      {renderSidebar && isLoggedIn ? (
        <>
          {/* ------------ tab & upwards ------------ */}
          <div
            className={`d-none d-sm-none d-md-none d-lg-block col-lg-1 col-xl-2 sidebar-container bg-white ${
              _.isEmpty(sideAdvtList)
                ? 'sidenav-regular  scrollbar-hide-no-maxheight'
                : ''
            }`}
          >
            <div
              className={
                !_.isEmpty(sideAdvtList) ? ` scrollbar-hide-no-maxheight` : ''
              }
              style={
                !_.isEmpty(sideAdvtList) && deviceSize != 1024
                  ? {
                      height: deviceSize.height - 78
                    }
                  : { height: '100%' }
              }
            >
              {_renderSideBar()}
            </div>

            {/* bottom ad */}
            {!_.isEmpty(sideAdvtList) ? (
              <div className="">
                <div
                  className="pt-2 pb-2"
                  id="advertisement"
                  style={{
                    borderRadius: 10,
                    marginBottom: 4
                  }}
                >
                  <Carousel
                    autoPlay={true}
                    infiniteLoop={true}
                    interval={sideMenuAdInterval}
                    showThumbs={false}
                    showArrows={false}
                    showStatus={false}
                    stopOnHover={false}
                    showIndicators={sideAdvtList.length > 1 ? true : false}
                    onClickItem={item => handleAdSelected(item)}
                    onClickThumb={item => handleAdSelected(item)}
                  >
                    {_.map(sideAdvtList, (ad, key) => {
                      if (ad.externallink) {
                        return (
                          <div key={`add_${key}`}>
                            <div
                              className=""
                              style={{
                                borderRadius: 10
                              }}
                            >
                              <a
                                href={ad.externallink}
                                target="_blank"
                                style={{
                                  borderRadius: 10
                                }}
                              >
                                <div className="">
                                  <LazyLoadImage
                                    effect="blur"
                                    placeholder={
                                      <img
                                        src={NoImgIcon}
                                        alt="lazy load placeholder on error"
                                      />
                                    }
                                    src={`${CDN_URL}${ad.bannerimg}`}
                                    alt={'ad goes here'}
                                    style={{
                                      borderRadius: 10
                                    }}
                                    onError={e => {
                                      e.target.onerror = null;
                                      e.target.src = NoImgIcon;
                                    }}
                                  />
                                </div>
                              </a>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div key={`add_${key}`}>
                            <div
                              className=""
                              style={{
                                borderRadius: 10
                              }}
                            >
                              <LazyLoadImage
                                effect="blur"
                                placeholder={
                                  <img
                                    src={NoImgIcon}
                                    alt="lazy load placeholder on error"
                                  />
                                }
                                src={`${CDN_URL}${ad.bannerimg}`}
                                alt="ad goes here"
                                style={{
                                  borderRadius: 10
                                }}
                                onError={e => {
                                  e.target.onerror = null;
                                  e.target.src = NoImgIcon;
                                }}
                              />
                            </div>
                          </div>
                        );
                      }
                    })}
                  </Carousel>
                </div>
              </div>
            ) : (
              ''
            )}
          </div>

          {/* ---------- mobile --------- */}
          <div
            ref={sidenavRef}
            className={`d-lg-none sidenav ${
              showSideBar ? 'sidenav-boxshadow' : ''
            }`}
            style={{
              transform:
                showSideBar == true ? 'translateX(0)' : 'translateX(-100%)'
            }}
          >
            {_renderSideBar()}

            {/* bottom ad */}
            {!_.isEmpty(sideAdvtList) ? (
              <div
                className="sidebar-container"
                style={{
                  paddingRight: 2,
                  paddingLeft: 2
                }}
              >
                <div
                  className="pt-2 pb-2"
                  id="advertisement"
                  style={{
                    borderRadius: 10
                  }}
                >
                  <Carousel
                    autoPlay={true}
                    infiniteLoop={true}
                    interval={sideMenuAdInterval}
                    showThumbs={false}
                    showArrows={false}
                    showStatus={false}
                    stopOnHover={false}
                    showIndicators={sideAdvtList.length > 1 ? true : false}
                    onClickItem={item => handleAdSelected(item)}
                    onClickThumb={item => handleAdSelected(item)}
                  >
                    {_.map(sideAdvtList, (ad, key) => {
                      if (ad.externallink) {
                        return (
                          <div key={`add_${key}`}>
                            <div
                              className=""
                              style={{
                                borderRadius: 10
                              }}
                            >
                              <a
                                href={ad.externallink}
                                target="_blank"
                                style={{
                                  borderRadius: 10
                                }}
                              >
                                <div className="">
                                  <LazyLoadImage
                                    effect="blur"
                                    placeholder={
                                      <img
                                        src={NoImgIcon}
                                        style={{
                                          height: 125,
                                          borderRadius: 10
                                        }}
                                        alt="lazy load placeholder on error"
                                      />
                                    }
                                    src={`${CDN_URL}${getThumbImageUrl(
                                      ad.bannerimg,
                                      'sm'
                                    )}`}
                                    alt="ad goes here"
                                    style={{
                                      height: 125,
                                      borderRadius: 10
                                    }}
                                    onError={e => {
                                      e.target.onerror = null;
                                      e.target.src = NoImgIcon;
                                    }}
                                  />
                                </div>
                              </a>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div key={`add_${key}`}>
                            <div
                              className=""
                              style={{
                                borderRadius: 10
                              }}
                            >
                              <LazyLoadImage
                                effect="blur"
                                placeholder={
                                  <img
                                    src={NoImgIcon}
                                    alt="lazy load placeholder on error"
                                  />
                                }
                                src={`${CDN_URL}${getThumbImageUrl(
                                  ad.bannerimg,
                                  'sm'
                                )}`}
                                alt="ad goes here"
                                style={{
                                  borderRadius: 10
                                }}
                                onError={e => {
                                  e.target.onerror = null;
                                  e.target.src = NoImgIcon;
                                }}
                              />
                            </div>
                          </div>
                        );
                      }
                    })}
                  </Carousel>
                </div>
              </div>
            ) : (
              ''
            )}
          </div>
        </>
      ) : (
        ''
      )}

      {/* ---------------- sidebar section end ---------------- */}

      {/* --------------------- main page --------------------- */}
      <div
        className={`col-12 col-sm-12 col-md-12 p-0 ${
          isLoggedIn ? 'col-lg-11 col-xl-10' : 'col-lg-12 col-xl-12'
        }`}
      >
        {/* ------------ top nav bar ------------ */}
        {!_.includes(window.location.hash, '#/autologin') &&
        !_.includes(window.location.hash, '#/sso-auth') ? (
          <AdminNavbar
            {...props}
            routes={routesNew}
            // brandText={getBrandText(location.pathname)}
            pathname={location.pathname}
            handleEditProfileToggle={handleEditProfileToggle}
          />
        ) : (
          ''
        )}

        {/* -------- main body -------- */}
        <div className="main-content full-height-WO-header">
          {_renderRoutes()}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const loadSurvey = !_.isNil(state.session?.userid);
  const sideAdvtList = !_.isEmpty(state.advtDetails)
    ? _.filter(state.advtDetails, advt => _.isEqual(advt.screen, 'sidemenu'))
    : [];

  const featureAppTabIds = {
    exhibitor: getAppTabId(state.eventTabs, ALLOWED_FEATURE_KEYS.exhibitor),
    sponsor: getAppTabId(state.eventTabs, ALLOWED_FEATURE_KEYS.sponsor),
    speaker: getAppTabId(state.eventTabs, ALLOWED_FEATURE_KEYS.speaker),
    schedule: getAppTabId(state.eventTabs, ALLOWED_FEATURE_KEYS.schedule)
  };

  const adSettings = state.eventSettings?.advertisment_settings;
  const sideMenuAdInterval = !_.isEmpty(adSettings)
    ? adSettings?.[ALLOWED_AD_INTERVALS.sidemenu] * 1000
    : 10 * 1000;

  const listSortType = getSectionListingSortType(
    state.eventSettings,
    ALLOWED_FEATURE_KEYS.attendee
  );

  const rolePermissions = getUserRolePrivilege(
    { ...state.userDetails, ...state.userDetailsAddOn },
    state.eventTabs,
    state.userAccessByRole,
    ROLE_PRIVILEGE_SPECIAL_CASES.ad
  );

  const featureSortType = {
    exhibitor: getSectionListingSortType(
      state.eventSettings,
      ALLOWED_FEATURE_KEYS.exhibitor
    ),
    sponsor: getSectionListingSortType(
      state.eventSettings,
      ALLOWED_FEATURE_KEYS.sponsor
    ),
    speaker: getSectionListingSortType(
      state.eventSettings,
      ALLOWED_FEATURE_KEYS.speaker
    )
  };

  return {
    isLoggedIn: state.isLoggedIn,
    session: state.session,
    eventDetails: state.eventDetails,
    userDetails: state.userDetails,
    showSideBar: state.showSidebar,
    deviceSize: state.deviceSize,
    eventDetails: state.eventDetails,
    profileToggleReducer: state.profileToggleReducer,
    attendeeList: state.attendeeList,
    appuserCategories: state.appuserCategories,
    interestList: state.interestList,
    sideAdvtList,
    loadSurvey,
    featureAppTabIds,
    sideMenuAdInterval,
    listSortType,
    rolePermissions,
    featureSortType,
    groupIds: getUserGroupIds(state.userDetails)
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setSponsor,
      clearAllSponsor,
      clearSponsorDetails,
      fetchAllSponsor,
      redirectingCalledFrom,
      setExhibitor,
      clearAllExhibitor,
      clearExhibitorDetails,
      fetchAllExhibitor,
      openSpeakerDetails,
      fetchAllSpeakers,
      postAnalytics,
      fetchAttendeeList,
      fetchGameChallengeStatus,
      setGamificationPopup,
      fetchUserAccess,
      fetchInterests,
      fetchAppuserCategory,
      activateNavbarLineLoader,
      makeEmpty_ScheduleData,
      makeEmpty_ScheduleTracks,
      makeEmpty_TrackSessions,
      fetchMinDataOfSelectedSchedule,
      fetchScheduleData,
      fetchBookedSchedule,
      selectScheduleDate,
      fetchScheduleTracks,
      selectScheduleTrack,
      fetchTrackSessions,
      setSessionDetailsAction
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(EventPortal);
